import SweepStagesModel from '../../../../../models/stagesModel';

import { ConnectStepsListGroup, ThirdPartyCurrentStep } from './types';
import { isSalesforceLink, isThirdPartyLink, stageToConnectStepItem } from './helper';
import { FunnelType } from '../../../../../types/enums/FunnelType';
import { CONNECT_STEPS_TAGS_COLORS } from './consts';
import { translateTypeToHumanName } from '../../../../documentation/helpers';

export const getGroupsForThirdPartyStep = ({
  step,
  funnelsData,
  thirdPartyFunnelsData,
  metadataElementsData,
  getObjectTypeLabel,
}: {
  step: ThirdPartyCurrentStep;
  funnelsData: FunnelMap['funnelsData'];
  thirdPartyFunnelsData: FunnelMap['thirdPartyFunnelsData'];
  metadataElementsData: FunnelMap['metadataElementsData'];
  getObjectTypeLabel: (crmOrgId: string, objectName: string) => string;
}) => {
  if (!step.stageId) {
    console.error('No stageId provided for third party step');
    return [];
  }
  const tpFunnel = thirdPartyFunnelsData[step.funnelId];
  const tpStep = tpFunnel.steps.find((s) => s.id === step.stageId);
  if (!tpFunnel || !tpStep) {
    return [];
  }
  const tpStepConnectionsToSteps = tpStep.stepConnections.map(({ targetId }) => targetId);

  const currentFunnelGroup: ConnectStepsListGroup = {
    value: tpFunnel.id,
    parentName: tpFunnel.name,
    label: 'Third party integration',
    tag: {
      type: 'other',
      tagText: 'Third party integration',
      tagColor: CONNECT_STEPS_TAGS_COLORS.thirdPartyIntegration,
    },
    funnelType: FunnelType.THIRD_PARTY,
    items: tpFunnel.steps
      .filter((s) => s.id !== tpStep.id && !tpStepConnectionsToSteps.includes(s.id))
      .map((s) => ({
        value: s.id,
        label: s.name,
      })),
  };

  const doesNotHaveSfStageFunnelLink = (funnelId: string) => (stage: SweepStage) => {
    return !Boolean(
      tpStep.funnelLinks
        ?.filter(isSalesforceLink)
        .find((link) => link.funnelId === funnelId && stage._stageId === link.stageId),
    );
  };

  const doesNotHaveThirdPartyStageFunnelLink = (funnelId: string) => (step: ThirdPartyStep) => {
    return !Boolean(
      tpStep.funnelLinks
        ?.filter(isThirdPartyLink)
        .find((link) => link.funnelId === funnelId && step.id === link.stageId),
    );
  };

  const hasStepToFunnelLink = (funnelId: string) => {
    return tpStep.funnelLinks?.some((link) => link.funnelId === funnelId && !link.stageId);
  };

  const allOtherSfFunnelGroups: ConnectStepsListGroup[] = Object.keys(funnelsData)
    .filter((funnelId) => funnelId !== tpFunnel.id)
    .map((funnelId) => {
      const objectNameLabel = getObjectTypeLabel(
        funnelsData[funnelId].crmOrgId,
        funnelsData[funnelId].funnelDetails.leadingObject.objectName,
      );
      return {
        value: funnelsData[funnelId].id,
        parentName: funnelsData[funnelId].name,
        label: objectNameLabel,
        tag: {
          type: 'object',
          objectName: objectNameLabel,
        },
        selectable: !hasStepToFunnelLink(funnelId),
        funnelType: FunnelType.SALESFORCE,
        items: new SweepStagesModel(funnelsData[funnelId].funnelDetails.stages)
          .nonLostSteps()
          .filter(doesNotHaveSfStageFunnelLink(funnelId))
          .map(stageToConnectStepItem),
      };
    });

  const allOtherThirdPartyFunnelGroups: ConnectStepsListGroup[] = Object.keys(thirdPartyFunnelsData)
    .filter((funnelId) => funnelId !== tpFunnel.id)
    .map((funnelId) => ({
      value: thirdPartyFunnelsData[funnelId].id,
      parentName: thirdPartyFunnelsData[funnelId].name,
      label: '3rd party integration',
      tag: {
        type: 'other',
        tagText: '3rd party integration',
        tagColor: CONNECT_STEPS_TAGS_COLORS.thirdPartyIntegration,
      },
      selectable: !hasStepToFunnelLink(funnelId),
      funnelType: FunnelType.THIRD_PARTY,
      items: thirdPartyFunnelsData[funnelId].steps
        .filter(doesNotHaveThirdPartyStageFunnelLink(funnelId))
        .map((step) => ({
          value: step.id,
          label: step.name,
        })),
    }));

  const hasFunnelLinkToMetadataElement = (metadataId: string) => {
    return Boolean(
      tpStep.funnelLinks?.some(
        (link) => link._type === FunnelType.METADATA_ELEMENT && link.funnelId === metadataId,
      ),
    );
  };

  const metadataElementsGroups: ConnectStepsListGroup[] = Object.entries(
    metadataElementsData || {},
  ).map(([metadataId, metadataElement]) => ({
    value: metadataId,
    parentName: metadataElement.name,
    label: 'Metadata element',
    tag: {
      type: 'other',
      tagText: translateTypeToHumanName(metadataElement.type),
      tagColor: CONNECT_STEPS_TAGS_COLORS.metadataElement,
    },
    selectable: !hasFunnelLinkToMetadataElement(metadataId),
    funnelType: FunnelType.METADATA_ELEMENT,
    items: [],
  }));

  return [
    currentFunnelGroup,
    ...allOtherSfFunnelGroups,
    ...allOtherThirdPartyFunnelGroups,
    ...metadataElementsGroups,
  ];
};

// Gets the groups and steps for the third party funnel
export const getGroupsForThirdPartyFunnel = ({
  step,
  funnelsData,
  thirdPartyFunnelsData,
  metadataElementsData,
  getObjectTypeLabel,
}: {
  step: ThirdPartyCurrentStep;
  funnelsData: FunnelMap['funnelsData'];
  thirdPartyFunnelsData: FunnelMap['thirdPartyFunnelsData'];
  metadataElementsData: FunnelMap['metadataElementsData'];
  getObjectTypeLabel: (crmOrgId: string, objectName: string) => string;
}) => {
  if (step.stageId !== undefined) {
    console.error(
      'Unexpected. Stage Id provided in getGroupsForThirdPartyFunnel. This isn\t a funnel.',
    );
    return [];
  }
  const tpFunnel = thirdPartyFunnelsData[step.funnelId];

  const doesNotHaveSfStageFunnelLink = (funnelId: string) => (stage: SweepStage) => {
    return !Boolean(
      tpFunnel.funnelLinks
        ?.filter(isSalesforceLink)
        .find((link) => link.funnelId === funnelId && stage._stageId === link.stageId),
    );
  };

  const hasFunnelToFunnelLink = (funnelId: string) => {
    return tpFunnel.funnelLinks?.some(
      (link) => link.funnelId === funnelId && link.stageId === undefined,
    );
  };

  const doesNotHaveThirdPartyStageFunnelLink = (funnelId: string) => (step: ThirdPartyStep) => {
    return !Boolean(
      tpFunnel.funnelLinks
        ?.filter(isThirdPartyLink)
        .find((link) => link.funnelId === funnelId && step.id === link.stageId),
    );
  };

  const allOtherSfFunnelGroups: ConnectStepsListGroup[] = Object.keys(funnelsData).map(
    (funnelId) => {
      const objectNameLabel = getObjectTypeLabel(
        funnelsData[funnelId].crmOrgId,
        funnelsData[funnelId].funnelDetails.leadingObject.objectName,
      );
      return {
        value: funnelsData[funnelId].id,
        parentName: funnelsData[funnelId].name,
        label: objectNameLabel,
        tag: {
          type: 'object',
          objectName: objectNameLabel,
        },
        selectable: !hasFunnelToFunnelLink(funnelId),
        funnelType: FunnelType.SALESFORCE,
        items: new SweepStagesModel(funnelsData[funnelId].funnelDetails.stages)
          .nonLostSteps()
          .filter(doesNotHaveSfStageFunnelLink(funnelId))
          .map(stageToConnectStepItem),
      };
    },
  );

  const allOtherThirdPartyFunnelGroups: ConnectStepsListGroup[] = Object.keys(thirdPartyFunnelsData)
    .filter((funnelId) => funnelId !== tpFunnel.id)
    .map((funnelId) => ({
      value: thirdPartyFunnelsData[funnelId].id,
      parentName: thirdPartyFunnelsData[funnelId].name,
      label: '3rd party integration',
      tag: {
        type: 'other',
        tagText: '3rd party integration',
        tagColor: CONNECT_STEPS_TAGS_COLORS.thirdPartyIntegration,
      },
      selectable: !hasFunnelToFunnelLink(funnelId),
      funnelType: FunnelType.THIRD_PARTY,
      items: thirdPartyFunnelsData[funnelId].steps
        .filter(doesNotHaveThirdPartyStageFunnelLink(funnelId))
        .map((step) => ({
          value: step.id,
          label: step.name,
        })),
    }));

  const hasFunnelLinkToMetadataElement = (metadataId: string) => {
    return Boolean(
      tpFunnel.funnelLinks?.some(
        (link) => link._type === FunnelType.METADATA_ELEMENT && link.funnelId === metadataId,
      ),
    );
  };

  const metadataElementsGroups: ConnectStepsListGroup[] = Object.entries(
    metadataElementsData || {},
  ).map(([metadataId, metadataElement]) => ({
    value: metadataId,
    parentName: metadataElement.name,
    label: 'Metadata element',
    tag: {
      type: 'other',
      tagText: translateTypeToHumanName(metadataElement.type),
      tagColor: CONNECT_STEPS_TAGS_COLORS.metadataElement,
    },
    selectable: !hasFunnelLinkToMetadataElement(metadataId),
    funnelType: FunnelType.METADATA_ELEMENT,
    items: [],
  }));

  return [...allOtherSfFunnelGroups, ...allOtherThirdPartyFunnelGroups, ...metadataElementsGroups];
};
